/* eslint-disable no-nested-ternary */
import * as vp from 'vopay';
import { FunctionComponent, useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import usePostTransactionsHistory from '../../../../hooks/useApi/useFetchTransactionsHistory';
import EftFundingTransactionContainer from './EftFundingTransactionContainer';
import EftWithdrawTransactionContainer from './EftWithdrawTransactionContainer';
import AchFundingTransactionContainer from './ACHFundingTransactionContainer';
import AchWithdrawalTransactionContainer from './ACHWithdrawalTransactionContainer';
import InteracMoneyRequestTransactionContainer from './InteracMoneyRequestTransactionContainer';
import InteracBulkPayoutTransactionContainer from './InteracBulkPayoutTransactionContainer';
import VopayInstantWithdrawalContainer from './VopayInstantWithdrawalContainer';
import RefundTransactionContainer from './RefundTransactionContainer';
import CurrencyConversionTransactionContainer from './CurrencyConversionTransactionContainer';
import InboundETransferContainer from './InboundETransferContainer';
import VisaDirectPushTransactionContainer from './VisaDirectPushTransactionContainer';
import CreditCardTransactionContainer from './CreditCardTransactionContainer';
import OtherTransactionContainer from './OtherTransactionContainer';

const { Table, Box, Title, SubTitle } = vp;

type TransactionInfoProps = {
  TransactionId: string;
};

type FC = FunctionComponent<TransactionInfoProps>;

type KeyValuePair = {
  display: string;
  value: string;
  visible?: boolean;
};

type TablePropsHandlers = {
  transactionDetail: () => vp.TableProps;
};

const ReversalTransactionInfo: FC = (props: TransactionInfoProps) => {
  const { t } = useTranslation('translations', {
    keyPrefix: 'Transaction.TransactionHistory.Details.ReversalTransactionInfo',
  });
  const { TransactionId } = props;
  const currentDate = moment().utc().format('YYYY-MM-DD');
  const [isResponseParent, setIsResponseParent] = useState(false);
  const [transactionValues, setTransactionValues] = useState<vp.TransactionsLists>();
  const [transactionValuesParent, setTransactionValuesParent] = useState<vp.TransactionsLists>();
  const [transactionsHistoryResponse, getTransactionsHistory] = usePostTransactionsHistory({});

  useEffect(() => {
    getTransactionsHistory({
      StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
      EndDateTime: currentDate,
      TransactionID: TransactionId,
    });
  }, [TransactionId, currentDate]);

  useEffect(() => {
    if (transactionsHistoryResponse.Success) {
      const transactionHistory = Object.values(transactionsHistoryResponse.Transactions);
      if (isResponseParent) {
        const transactionValuesNew = transactionHistory.find(
          (x) => x.TransactionID === transactionValues?.ParentTransactionID,
        );
        setIsResponseParent(false);
        setTransactionValuesParent(transactionValuesNew);
      } else {
        const transactionValuesNew = transactionHistory.find((x) => x.TransactionType === 'Reversal');
        setTransactionValues(transactionValuesNew);
        setIsResponseParent(true);

        if (transactionValuesNew?.ParentTransactionID) {
          getTransactionsHistory({
            StartDateTime: moment().subtract(5, 'years').format('YYYY-MM-DD'),
            EndDateTime: currentDate,
            TransactionID: transactionValuesNew?.ParentTransactionID,
          });
        }
      }
    }
  }, [transactionsHistoryResponse]);

  return (
    <>
      <Box mt="lg" mb="md">
        <SubTitle subTitle={t('transactionDetailsTitle') || ''} />
      </Box>
      <Table {...getTableProps('transactionDetail')} />
      <Box mt="lg" mb="md">
        <Title title={t('parentTransactionOverview')} />
      </Box>
      <>
        {transactionValuesParent?.TransactionType === vp.TransactionType.EFT_FUNDING ? (
          <EftFundingTransactionContainer
            TransactionID={transactionValuesParent?.TransactionID}
            Notes={transactionValuesParent?.Notes}
          />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.EFT_WITHDRAWAL ? (
          <EftWithdrawTransactionContainer
            TransactionID={transactionValuesParent?.TransactionID}
            Notes={transactionValuesParent?.Notes}
          />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.ACH_FUNDING ? (
          <AchFundingTransactionContainer
            TransactionID={transactionValuesParent?.TransactionID}
            Notes={transactionValuesParent?.Notes}
          />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.ACH_WITHDRAWAL ? (
          <AchWithdrawalTransactionContainer
            TransactionID={transactionValuesParent?.TransactionID}
            Notes={transactionValuesParent?.Notes}
          />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.INTERACT_MONEY_REQUEST ? (
          <InteracMoneyRequestTransactionContainer
            TransactionID={transactionValuesParent?.TransactionID}
            Notes={transactionValuesParent?.Notes}
          />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.INTERACT_BULK_PAYOUT ? (
          <InteracBulkPayoutTransactionContainer
            TransactionID={transactionValuesParent?.TransactionID}
            Notes={transactionValuesParent?.Notes}
          />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.VOPAYINSTANT_WITHDRAWAL ? (
          <VopayInstantWithdrawalContainer
            TransactionID={transactionValuesParent?.TransactionID}
            Notes={transactionValuesParent?.Notes}
          />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.REFUND ? (
          <RefundTransactionContainer TransactionID={transactionValuesParent?.TransactionID} />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.CURRENCY_CONVERSION ? (
          <CurrencyConversionTransactionContainer TransactionID={transactionValuesParent?.TransactionID} />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.INBOUND_E_TRANSFER ? (
          <InboundETransferContainer TransactionID={transactionValuesParent?.TransactionID} />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.VISA_DIRECT_PUSH ? (
          <VisaDirectPushTransactionContainer TransactionID={transactionValuesParent?.TransactionID} />
        ) : transactionValuesParent?.TransactionType === vp.TransactionType.CREDIT_CARD ? (
          <CreditCardTransactionContainer TransactionID={transactionValuesParent?.TransactionID} />
        ) : (
          <OtherTransactionContainer />
        )}
      </>
    </>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      transactionDetail: () => {
        const transactionDetailsPairs: KeyValuePair[] = [
          {
            display: t('transactionDetails.transactionID'),
            value: transactionValues?.TransactionID || '',
            visible: true,
          },
          {
            display: t('transactionDetails.parentTransactionID'),
            value: transactionValues?.ParentTransactionID || '',
            visible: true,
          },
          {
            display: t('transactionDetails.transactionDate'),
            value: transactionValues?.TransactionDateTime || '',
            visible: true,
          },
          {
            display: t('transactionDetails.lastModified'),
            value: transactionValues?.LastModified || '',
            visible: true,
          },
          {
            display: t('transactionDetails.type'),
            value: t('reversal') || '',
            visible: true,
          },
          {
            display: t('transactionDetails.debitAmount'),
            value: `$${transactionValues?.DebitAmount || ''}`,
            visible: true,
          },
          {
            display: t('transactionDetails.creditAmount'),
            value: `$${transactionValues?.CreditAmount || ''}`,
            visible: true,
          },
          {
            display: t('transactionDetails.holdAmount'),
            value: `$${transactionValues?.HoldAmount || ''}`,
            visible: true,
          },
          {
            display: t('transactionDetails.currency'),
            value: transactionValues?.Currency || '',
            visible: true,
          },
          {
            display: t('transactionDetails.notes'),
            value: transactionValues?.Notes || '',
            visible: !!transactionValues?.Notes,
          },
        ];

        const transactionDetailsProps: vp.TableProps = {
          name: 'transaction-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('transactionDetail') || '' },
            detailValue: { header: '' },
          },
          rows: transactionDetailsPairs
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return transactionDetailsProps;
      },
    };

    return handlers[handler]();
  }
};

export default ReversalTransactionInfo;
